// Array manipulation utilities

/**
 * Remove duplicates from array based on key function
 * @param array Array to deduplicate
 * @param keyFn Function to extract comparison key
 * @returns Array with duplicates removed
 */
export function uniqueBy<T, K>(array: T[], keyFn: (item: T) => K): T[] {
	const seen = new Set<K>();
	return array.filter((item) => {
		const key = keyFn(item);
		if (seen.has(key)) {
			return false;
		}
		seen.add(key);
		return true;
	});
}

/**
 * Group array items by key function
 * @param array Array to group
 * @param keyFn Function to extract grouping key
 * @returns Object with grouped items
 */
export function groupBy<T, K extends string | number>(
	array: T[],
	keyFn: (item: T) => K,
): Record<K, T[]> {
	return array.reduce(
		(groups, item) => {
			const key = keyFn(item);
			if (!groups[key]) {
				groups[key] = [];
			}
			groups[key].push(item);
			return groups;
		},
		{} as Record<K, T[]>,
	);
}

/**
 * Sort array by multiple criteria
 * @param array Array to sort
 * @param sortFns Array of sort functions with direction
 * @returns Sorted array
 */
export function sortBy<T>(
	array: T[],
	sortFns: Array<{
		fn: (item: T) => any;
		direction?: "asc" | "desc";
	}>,
): T[] {
	return [...array].sort((a, b) => {
		for (const { fn, direction = "asc" } of sortFns) {
			const aVal = fn(a);
			const bVal = fn(b);

			let comparison = 0;
			if (aVal < bVal) comparison = -1;
			else if (aVal > bVal) comparison = 1;

			if (comparison !== 0) {
				return direction === "desc" ? -comparison : comparison;
			}
		}
		return 0;
	});
}

/**
 * Chunk array into smaller arrays of specified size
 * @param array Array to chunk
 * @param size Size of each chunk
 * @returns Array of chunks
 */
export function chunk<T>(array: T[], size: number): T[][] {
	const chunks: T[][] = [];
	for (let i = 0; i < array.length; i += size) {
		chunks.push(array.slice(i, i + size));
	}
	return chunks;
}

/**
 * Move item from one index to another in array
 * @param array Array to modify
 * @param fromIndex Source index
 * @param toIndex Destination index
 * @returns New array with item moved
 */
export function moveItem<T>(
	array: T[],
	fromIndex: number,
	toIndex: number,
): T[] {
	const result = [...array];
	const [removed] = result.splice(fromIndex, 1);
	if (removed !== undefined) {
		result.splice(toIndex, 0, removed);
	}
	return result;
}
